import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const INTERRUPTOR_DEFERRED_CARDS = 2;

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {HTMLElement[]} */
  #deferredCards = [];

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#deferPage1CardsForInterruptor();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * When the collection interruptor block is active in load-more mode,
   * trims the visible page-1 grid by two cards so the initial view stays
   * aligned with products_per_page - 2. The trimmed cards are re-attached
   * on the first load-more click.
   */
  #deferPage1CardsForInterruptor() {
    if (this.getAttribute('pagination-mode') !== 'load_more') return;
    if (!this.querySelector('.product-grid__item--interruptor')) return;

    const page1Cards = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[ref="cards[]"][data-page="1"]'))
    );

    if (page1Cards.length <= INTERRUPTOR_DEFERRED_CARDS) return;

    this.#deferredCards = page1Cards.slice(-INTERRUPTOR_DEFERRED_CARDS);
    for (const card of this.#deferredCards) {
      card.hidden = true;
    }

    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    this.#bumpLoadMoreCount(loadMoreButton, INTERRUPTOR_DEFERRED_CARDS);
    loadMoreButton.addEventListener('click', this.#revealDeferredCards, { once: true });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {number} addend
   */
  #bumpLoadMoreCount(button, addend) {
    const current = Number(button.dataset.remaining ?? 0);
    const next = current + addend;
    button.dataset.remaining = String(next);
    button.textContent = button.textContent.replace(/\(\d+\)/, `(${next})`);
  }

  #revealDeferredCards = () => {
    for (const card of this.#deferredCards) {
      card.hidden = false;
    }
    this.#deferredCards = [];
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
